import { db } from "../config";
import {
  collection,
  query,
  where,
  getDocs,
  orderBy,
  limit,
} from "firebase/firestore";
import { getBookById } from "./bookServices";
import { getAndStoreBook } from "../../services/googleBooksApi";

export const searchUsers = async (
  searchTerm,
  startIndex = 0,
  maxResults = 20
) => {
  try {
    const usersRef = collection(db, "Users");
    const searchTermLower = searchTerm.toLowerCase();

    // Search by username or display name
    const usernameQuery = query(
      usersRef,
      where("usernameLower", ">=", searchTermLower),
      where("usernameLower", "<=", searchTermLower + "\uf8ff"),
      limit(maxResults)
    );

    const displayNameQuery = query(
      usersRef,
      where("displayNameLower", ">=", searchTermLower),
      where("displayNameLower", "<=", searchTermLower + "\uf8ff"),
      limit(maxResults)
    );

    const [usernameResults, displayNameResults] = await Promise.all([
      getDocs(usernameQuery),
      getDocs(displayNameQuery),
    ]);

    // Combine and deduplicate results
    const userMap = new Map();

    [...usernameResults.docs, ...displayNameResults.docs].forEach((doc) => {
      if (!userMap.has(doc.id)) {
        userMap.set(doc.id, {
          uid: doc.id,
          ...doc.data(),
        });
      }
    });

    // Convert to array and handle pagination
    const allResults = Array.from(userMap.values());
    const paginatedResults = allResults.slice(
      startIndex,
      startIndex + maxResults
    );
    return paginatedResults;
  } catch (error) {
    console.error("Error searching users:", error);
    throw error;
  }
};

export const searchLists = async (
  searchTerm,
  startIndex = 0,
  maxResults = 20
) => {
  try {
    const listsRef = collection(db, "ReadingLists");
    const searchTermLower = searchTerm.toLowerCase();

    const listsQuery = query(
      listsRef,
      where("nameLower", ">=", searchTermLower),
      where("nameLower", "<=", searchTermLower + "\uf8ff"),
      orderBy("nameLower"),
      // Fetch extra items to handle pagination after processing
      limit(startIndex + maxResults)
    );

    const querySnapshot = await getDocs(listsQuery);

    // Get full list details including books
    const listsWithDetails = await Promise.all(
      querySnapshot.docs.map(async (doc) => {
        const listData = doc.data();

        // Fetch book details for each book ID
        const books = await Promise.all(
          listData.books.map(async (bookId) => {
            try {
              // First try to get from our cache
              let book = await getBookById(bookId);

              // If not in cache, fetch from Google Books and store
              if (!book) {
                book = await getAndStoreBook(bookId);
              }

              return book;
            } catch (error) {
              console.error("Error fetching book:", bookId, error);
              return null;
            }
          })
        );

        // Filter out any null values and add metadata
        const validBooks = books.filter(Boolean).map((book) => ({
          ...book,
          dateAdded: book.createdAt || new Date().toISOString(),
          pageCount: book.pageCount || 0,
          publishedDate: book.publishedDate || "",
          averageRating: book.rating || 0,
        }));

        return {
          id: doc.id,
          ...listData,
          books: validBooks,
        };
      })
    );

    // Handle pagination after processing
    return listsWithDetails.slice(startIndex, startIndex + maxResults);
  } catch (error) {
    console.error("Error searching lists:", error);
    throw error;
  }
};
